import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, Mail, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { AuthShell } from "@/components/AuthShell";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { apiCall } from "@/lib/shopApi";
import { useApiError } from "@/lib/useApiError";

const NIET_GEBOUWD = [404, 405, 501];

const WachtwoordVergeten = () => {
  const { t, i18n } = useTranslation();
  const toMessage = useApiError();
  const [email, setEmail] = useState("");
  const [verstuurd, setVerstuurd] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await apiCall("POST", "/shop/auth/wachtwoord-vergeten", {
        body: {
          email: email.trim().toLowerCase(),
          taal: i18n.language,
          reset_url: `${window.location.origin}/wachtwoord-reset`,
        },
      });
      setVerstuurd(true);
    } catch (err) {
      if (NIET_GEBOUWD.includes(err?.status)) setError(t("auth.forgot.unavailable"));
      else setError(toMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t("auth.forgot.title")} subtitle={t("auth.forgot.subtitle")} testId="forgot-password-page">
        {verstuurd ? (
          <div className="space-y-5">
            <Alert data-testid="forgot-password-sent-alert">
              <AlertDescription>{t("auth.forgot.sent")}</AlertDescription>
            </Alert>
            <Button asChild variant="outline" className="w-full" data-testid="forgot-password-back-link">
              <Link to="/login">
                <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                {t("auth.forgot.backToLogin")}
              </Link>
            </Button>
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="forgot-password-error-alert">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <div className="space-y-2">
              <Label htmlFor="forgot-email">{t("auth.login.fields.email")}</Label>
              <Input
                id="forgot-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="forgot-password-email-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={submitting} data-testid="forgot-password-submit-button">
              {submitting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Mail className="mr-2 h-4 w-4" aria-hidden="true" />
              )}
              {submitting ? t("auth.forgot.submitting") : t("auth.forgot.submit")}
            </Button>
            <p className="text-center text-sm">
              <Link to="/login" className="font-medium text-primary hover:underline" data-testid="forgot-password-login-link">
                {t("auth.forgot.backToLogin")}
              </Link>
            </p>
          </form>
        )}
    </AuthShell>
  );
};

export default WachtwoordVergeten;
